import { useCallback, useEffect, useRef } from 'react';
import { Control, UseFormReturn, useWatch } from 'react-hook-form';

import { SettlementDTO } from '~formsShared/components/SettlementFormFields';
import { PaymentTerm } from '~hooks/usePaymentTerms/models';
import { Settlement } from '~hooks/useSettlements/models';
import useCompanyDefaultPaymentTermIdQuery from '~queries/useCompanyDefaultPaymentTermIdQuery';
import usePaymentTermsQuery from '~queries/usePaymentTermsQuery';
import { useStores } from '~store';
import { Nullable } from '~types/Nullable';
import {
  buildPaymentTermOptions,
  dueDateMatchesPaymentTerm,
  getPaymentTermDueDate,
  NO_PAYMENT_TERM,
} from '~utils/settlements/paymentTerms';

interface UseSettlementPaymentTermProps {
  companyId?: string;
  control: Control<SettlementDTO>;
  getFieldState: UseFormReturn<SettlementDTO>['getFieldState'];
  getValues: UseFormReturn<SettlementDTO>['getValues'];
  resetField: UseFormReturn<SettlementDTO>['resetField'];
  savedPaymentTermId: string;
  settlement?: Settlement;
  setValue: UseFormReturn<SettlementDTO>['setValue'];
}

export default function useSettlementPaymentTerm({
  companyId,
  control,
  getFieldState,
  getValues,
  resetField,
  savedPaymentTermId,
  settlement,
  setValue,
}: UseSettlementPaymentTermProps) {
  const { userStore } = useStores();
  const isCreating = !settlement?.id;
  const userCompanyId = userStore.userCompany.id;
  const companyIsSignedInCompany = companyId === userCompanyId;

  const { isFetchingPaymentTerms, paymentTerms } = usePaymentTermsQuery({ companyId });
  // the store only carries the signed-in company's default
  const {
    defaultPaymentTermId: selectedCompanyDefaultPaymentTermId,
    isFetchingDefaultPaymentTermId,
  } = useCompanyDefaultPaymentTermIdQuery({
    companyId: isCreating && !companyIsSignedInCompany ? companyId : undefined,
  });
  const defaultPaymentTermId = companyIsSignedInCompany
    ? userStore.userCompany.defaultPaymentTermId
    : selectedCompanyDefaultPaymentTermId;

  const isWaitingForDefaultPaymentTerm =
    isCreating && (isFetchingPaymentTerms || isFetchingDefaultPaymentTermId);

  const watchedPaymentTermId = useWatch({ control, name: 'paymentTermId' });
  const previousPaymentTermIdRef = useRef<Nullable<string> | undefined>(
    savedPaymentTermId,
  );

  const dueDateForPaymentTerm = useCallback(
    (term: PaymentTerm) => getPaymentTermDueDate(term, settlement?.createdAt),
    [settlement?.createdAt],
  );

  useEffect(() => {
    if (
      !isCreating ||
      !companyId ||
      isWaitingForDefaultPaymentTerm ||
      getFieldState('paymentTermId').isDirty
    ) {
      return;
    }

    const defaultTerm = paymentTerms.find((term) => term.id === defaultPaymentTermId);
    const seededTerm = defaultTerm?.active ? defaultTerm : undefined;
    const seededPaymentTermId = seededTerm?.id ?? NO_PAYMENT_TERM;

    previousPaymentTermIdRef.current = seededPaymentTermId;
    // seeded as the field's default so a later switch to "None" still counts as a change
    resetField('paymentTermId', { defaultValue: seededPaymentTermId });

    // a date the user typed stays; one seeded for the previous company is replaced
    if (!getFieldState('dueDate').isDirty) {
      setValue('dueDate', seededTerm ? dueDateForPaymentTerm(seededTerm) : null, {
        shouldDirty: false,
      });
    }
  }, [
    companyId,
    defaultPaymentTermId,
    dueDateForPaymentTerm,
    getFieldState,
    isCreating,
    isWaitingForDefaultPaymentTerm,
    paymentTerms,
    resetField,
    setValue,
  ]);

  useEffect(() => {
    // Read the live value: the seeding effect above resets the field in the same commit,
    // before the watched value has caught up.
    const currentPaymentTermId = getValues('paymentTermId');

    if (currentPaymentTermId === previousPaymentTermIdRef.current) {
      return;
    }

    previousPaymentTermIdRef.current = currentPaymentTermId;

    if (!isCreating && currentPaymentTermId === savedPaymentTermId) {
      setValue('dueDate', settlement?.dueDate ?? null, { shouldDirty: false });

      return;
    }

    const selectedTerm = paymentTerms.find((term) => term.id === currentPaymentTermId);

    if (selectedTerm) {
      setValue('dueDate', dueDateForPaymentTerm(selectedTerm), { shouldDirty: false });
    }
  }, [
    dueDateForPaymentTerm,
    getValues,
    isCreating,
    paymentTerms,
    savedPaymentTermId,
    setValue,
    settlement?.dueDate,
    watchedPaymentTermId,
  ]);

  // On create the term is always stated so the API cannot apply a default the form did not
  // show; on edit it travels only when changed. A date matching the term-derived one is
  // omitted so the server derives it (on edit only once the term changed, since an absent
  // date there means "keep the stored one").
  const toSubmitPayload = useCallback(
    (values: SettlementDTO): SettlementDTO => {
      const paymentTermWasChanged = getFieldState('paymentTermId').isDirty;
      const selectedTerm = paymentTerms.find((term) => term.id === values.paymentTermId);
      const shouldSendPaymentTerm = isCreating || paymentTermWasChanged;
      const serverDerivesDueDate =
        dueDateMatchesPaymentTerm(values.dueDate, selectedTerm, settlement?.createdAt) &&
        shouldSendPaymentTerm;

      return {
        ...values,
        dueDate: serverDerivesDueDate ? undefined : values.dueDate,
        paymentTermId: shouldSendPaymentTerm ? values.paymentTermId || null : undefined,
      };
    },
    [getFieldState, isCreating, paymentTerms, settlement?.createdAt],
  );

  const paymentTermOptions = buildPaymentTermOptions({
    paymentTerms,
    savedPaymentTermId,
    savedPaymentTermSnapshot: {
      name: settlement?.paymentTermName ?? '',
      dueDays: settlement?.paymentTermDueDays ?? 0,
    },
  });

  return {
    isFetchingPaymentTerms,
    isWaitingForDefaultPaymentTerm,
    paymentTermOptions,
    toSubmitPayload,
  };
}
